'use client';

import React, { useEffect, useRef, useState } from 'react';
import { BookOpen, Check, Globe, Palette, Pencil, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { getOfferedLanguageOptions } from '@/utils/languages';
import { getResolvedStoryPageCounts, loadStoryPageCounts } from '@/lib/pricing/story-page-counts-client';
import { PICTURE_BOOK_FLOW_STYLES, pictureBookStyleName } from '@/utils/manga/picture-book-style';
import { inferReaderBand } from '@/utils/manga/comic-story-profile';
import { pictureBookTemplate, templateIdeaFor, templateTitleFor, templateTitleIsFixed } from '@/lib/picture-book/templates';
import type { UnifiedStoryTemplate } from '@/lib/unified-story-templates';
import type { PictureBookStyle } from '@/types/manga';
import type { PhotoScene } from '@/utils/picture-book/photo-scene';
import type { MangaCharacterDraft } from '@/components/manga/types';
import { trackCreate } from '@/utils/create-flow-analytics';
import { fetchSuggestion, type SuggestionAnswer, type SuggestionRequest } from './suggestions-client';
import { IdeaCardPicker } from '@/components/create-flow/IdeaCardPicker';
import { SURPRISE_IDEA, ideaById, ideaCardsFor, type FilledIdea, type IdeaOccasion } from '@/lib/catalogue/idea-cards';
import { PictureBookTemplatePicker } from './PictureBookTemplatePicker';
import { TypedRelativeNotice, useTypedRelativeGate } from '@/components/manga/typed-relative-gate';
import { centralRoleRefusal, unfilledCentralRoles } from '@/lib/picture-book/central-roles';
import type { TypedRelativesChoice } from '@/utils/manga/family-guard';
import {
  cardDetails,
  defaultPageCountForBand,
  fieldsToSuggest,
  ideaSourceOf,
  refillFromCard,
  suggestionEventsKey,
  languageCheckFields,
  pictureBookTypedRelatives,
  suggestionEvents,
  suggestionMaySetStyle,
  suggestionPeopleKey,
  withParentStyle,
  titleIsParents,
  type FieldSource,
  type PictureBookPageCount,
  type SuggestionField,
  FIXED_TITLE_NOTE,
} from './flow-state';

export interface DetailsValue {
  style: PictureBookStyle;
  title: string;
  idea: string;
  pageCount: PictureBookPageCount;
  language: string;
  templateId?: string;
  source: Record<SuggestionField, FieldSource>;
  // Bookkeeping held by the flow so Back then Next never re-asks for suggestions or overwrites the parent's text.
  /** The latest suggestion per field (the baseline for suggestion_kept / suggestion_edited). */
  suggested?: { style: PictureBookStyle; title: string; idea: string } | null;
  suggestionSource?: SuggestionAnswer['source'];
  /** suggestionPeopleKey (people, scene, language) the suggestion was made for; a change re-asks unedited fields. */
  suggestionFor?: string;
  /** P2-R6: fields the parent confirmed as right in their language. */
  confirmed?: Partial<Record<'title' | 'idea', boolean>>;
  /** The book language when the parent confirmed; a later language change voids every confirmation. */
  confirmedLanguage?: string;
  shuffles?: number;
  /** Free-cover continue: the title/idea we seeded from the cover. While the field still equals it, no new suggestion replaces it. */
  coverSeed?: { title: string | null; idea: string | null };
  /** The parent picked a length: the band default no longer applies. */
  pageCountChosen?: boolean;
  /** The parent's answer to the typed-relative notice (Task C3); it only counts for the words it was given for. */
  typedRelatives?: TypedRelativesChoice | null;
  /** The idea card that filled the title and idea (make-up-your-own Task 5); unset by Surprise me or a ready-made story. */
  ideaCard?: string;
  /** suggestionEventsKey when suggestion_kept/edited were last sent: Back then Next with nothing changed sends none. */
  eventsSent?: string;
}

export function emptyDetailsValue(language = 'en'): DetailsValue {
  return {
    style: 'watercolor',
    title: '',
    idea: '',
    pageCount: 16,
    language,
    source: { style: 'suggestion', title: 'suggestion', idea: 'suggestion' },
    suggested: null,
  };
}

interface PictureBookDetailsStepProps {
  people: MangaCharacterDraft[];
  scene: PhotoScene | null;
  value: DetailsValue;
  onChange: (value: DetailsValue) => void;
  onBack: () => void;
  onNext: () => void;
  /** The flow is checking a stored preview book (Next pressed): Next is disabled meanwhile. */
  busy?: boolean;
  /** "Use a different photo" (typed-relative and central-role notices): back to the photo step. */
  onChangePhoto?: () => void;
  /**
   * Catalogue display names by template id (server-provided; the catalogue stays out of this bundle), used for the
   * central-role notice title like the create route does. A template not listed falls back to its template title.
   */
  storyNames?: Readonly<Record<string, string>>;
  /** `?idea=` (validated by the page): that card fills the step on the first visit instead of an AI suggestion. */
  pinnedIdea?: string;
  /** `?occasion=`: that occasion's ideas come first (occasion-only ones are offered only then). */
  occasion?: IdeaOccasion;
}

/** Style previews made by the picture-book engine (scripts/picture-book-engine/style-previews.mjs). */
const STYLE_PREVIEWS: Record<PictureBookStyle, string> = {
  watercolor: '/styles/picture-book/watercolor.png',
  european: '/styles/picture-book/european.png',
  impressionist: '/styles/picture-book/impressionist.png',
  'oil-painting': '/styles/picture-book/oil-painting.png',
  anime: '/styles/picture-book/anime.png',
  graphicnovel: '/styles/picture-book/graphicnovel.png',
};

const THINKING = 'Thinking of ideas…';
const CHECK_LANGUAGE = 'Please check this in your language';
const ALL_PAGE_COUNTS: readonly PictureBookPageCount[] = [8, 16, 24];

const suggestionCharacters = (people: MangaCharacterDraft[]): SuggestionRequest['characters'] =>
  people.map((p) => ({
    name: p.name.trim(),
    age: Number(p.age) || 0,
    gender: p.gender,
    ...(!p.isMainCharacter && p.relationship.trim() ? { relationship: p.relationship.trim() } : {}),
    ...(p.isMainCharacter ? { isMainCharacter: true } : {}),
  }));

const castFor = (people: MangaCharacterDraft[]) =>
  people.map((p) => ({ name: p.name.trim(), age: p.age, isMainCharacter: p.isMainCharacter }));

export function PictureBookDetailsStep({
  people,
  scene,
  value,
  onChange,
  onBack,
  onNext,
  busy = false,
  onChangePhoto = onBack,
  storyNames,
  pinnedIdea,
  occasion,
}: PictureBookDetailsStepProps) {
  const { toast } = useToast();
  const band = inferReaderBand(people);
  const peopleKey = suggestionPeopleKey(people, scene, value.language);
  const ideaCast = people.map((p) => ({ name: p.name, age: p.age, relationship: p.relationship, isMainCharacter: p.isMainCharacter }));
  /**
   * The idea cards for this family. The current card is pinned (tier 0) so an eligible pick always stays listed and is
   * filled with the current names; else the ?idea card. A card that is no longer eligible drops out (the people
   * effect then re-asks, so its stale names never stay).
   */
  const cardsFor = (current: string | undefined) =>
    ideaCardsFor({
      product: 'picture-book',
      cast: ideaCast,
      band,
      occasion,
      pinned: current ?? (pinnedIdea !== SURPRISE_IDEA ? pinnedIdea : undefined),
    });
  const cards = cardsFor(value.ideaCard);
  const [editingIdea, setEditingIdea] = useState(false);
  // Set by "Edit the idea": the textarea that replaces the button takes the focus
  const [focusIdea, setFocusIdea] = useState(false);

  // Async answers merge into the latest value, never into the one captured when the call started.
  const valueRef = useRef(value);
  valueRef.current = value;
  // Two effects may change the value in one tick: each builds on the last change, not on the render's value.
  const commit = (next: DetailsValue) => {
    valueRef.current = next;
    onChange(next);
  };
  const mounted = useRef(true);
  const requestId = useRef(0);
  const [pending, setPending] = useState<Array<'title' | 'idea'>>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [allowedCounts, setAllowedCounts] = useState<number[]>(() => getResolvedStoryPageCounts() ?? [16, 24]);

  const firstFill = !value.suggested;
  const titlePending = firstFill || pending.includes('title');
  const ideaPending = firstFill || pending.includes('idea');
  const thinking = titlePending || ideaPending;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadStoryPageCounts().then((counts) => {
      if (!cancelled) setAllowedCounts(counts);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Page count: the band's default until the parent picks one; a length not offered here becomes 16.
  useEffect(() => {
    const v = valueRef.current;
    const next = v.pageCountChosen
      ? allowedCounts.includes(v.pageCount) ? v.pageCount : 16
      : defaultPageCountForBand(band, allowedCounts);
    if (next !== v.pageCount) commit({ ...v, pageCount: next });
  }, [band, allowedCounts.join(',')]);

  const ask = async (fields: Array<'title' | 'idea'>, extra: Partial<SuggestionRequest>) => {
    const id = ++requestId.current;
    setPending(fields);
    const answer = await fetchSuggestion({
      characters: suggestionCharacters(people),
      band,
      language: valueRef.current.language || 'en',
      scene,
      ...extra,
    });
    if (!mounted.current || id !== requestId.current) return null;
    setPending([]);
    return answer;
  };

  // First visit, or the people / scene changed: ask for suggestions for the fields that are not the parent's.
  useEffect(() => {
    const v = valueRef.current;
    if (v.suggestionFor === peopleKey) return;
    // An unedited idea card is filled again with the new names: no AI call
    if (v.ideaCard) {
      const refilled = refillFromCard(v, cardsFor(v.ideaCard).find((c) => c.id === v.ideaCard));
      if (refilled) {
        commit({ ...refilled, suggestionFor: peopleKey });
        return;
      }
    }
    // ?idea= on the first visit: that card fills the step instead of an AI suggestion (?story wins: a template is kept)
    if (!v.suggested && !v.templateId && pinnedIdea && pinnedIdea !== SURPRISE_IDEA) {
      const card = cardsFor(undefined).find((c) => c.id === pinnedIdea);
      if (card) {
        commit({ ...cardDetails(v, card), suggestionFor: peopleKey });
        return;
      }
    }
    // ?idea=surprise with an ?occasion: a random card for that occasion (with no occasion the AI suggestion is the surprise)
    if (!v.suggested && !v.templateId && pinnedIdea === SURPRISE_IDEA && occasion) {
      const forOccasion = cardsFor(undefined).filter((c) => ideaById(c.id, 'picture-book')?.occasion === occasion);
      if (forOccasion.length) {
        const card = forOccasion[Math.min(forOccasion.length - 1, Math.floor(Math.random() * forOccasion.length))];
        commit({ ...cardDetails(v, card), suggestionFor: peopleKey });
        return;
      }
    }
    // A cover-seeded title/idea stays until the parent changes it (or shuffles)
    const fields = fieldsToSuggest(v).filter((f) => !(v.coverSeed?.[f] && v[f] === v.coverSeed[f]));
    const titleOwned = !fields.includes('title');
    // A template title carries the names: re-personalize it for the new cast.
    const tpl = v.templateId && v.source.title === 'template' ? pictureBookTemplate(v.templateId) : undefined;
    const retitled = tpl ? templateTitleFor(tpl, castFor(people)) : v.title;
    if (fields.length === 0) {
      commit({ ...v, title: retitled, suggestionFor: peopleKey });
      return;
    }
    const keepTitle = titleOwned && v.source.title !== 'template' ? v.title.trim() : undefined;
    ask(fields, keepTitle ? { keepTitle } : {}).then((answer) => {
      if (!answer) return;
      const cur = valueRef.current;
      const styleUntouched = suggestionMaySetStyle(cur);
      commit({
        ...cur,
        style: styleUntouched ? answer.style : cur.style,
        title: fields.includes('title') ? answer.title : retitled,
        idea: fields.includes('idea') ? answer.idea : cur.idea,
        source: {
          style: styleUntouched ? 'suggestion' : cur.source.style,
          title: fields.includes('title') ? 'suggestion' : cur.source.title,
          idea: fields.includes('idea') ? 'suggestion' : cur.source.idea,
        },
        suggested: {
          style: styleUntouched ? answer.style : cur.suggested?.style ?? answer.style,
          title: fields.includes('title') ? answer.title : cur.suggested?.title ?? answer.title,
          idea: fields.includes('idea') ? answer.idea : cur.suggested?.idea ?? answer.idea,
        },
        suggestionSource: answer.source,
        suggestionFor: peopleKey,
        // The AI text replaced (part of) the card's, so it no longer came from a card
        ideaCard: undefined,
        // Re-asked fields lose their confirmation; a confirmed field that was kept stays confirmed.
        confirmed: { ...(fields.includes('title') ? {} : { title: cur.confirmed?.title }), ...(fields.includes('idea') ? {} : { idea: cur.confirmed?.idea }) },
      });
    });
  }, [peopleKey]);

  const handleShuffle = async () => {
    const v = valueRef.current;
    trackCreate('picture-book', 'idea_shuffled');
    const keep = titleIsParents({ title: v.title, source: v.source.title }, v.suggested?.title) ? v.title.trim() : undefined;
    const shuffles = (v.shuffles ?? 0) + 1;
    const avoidIdeas = [...new Set([v.idea, v.suggested?.idea].filter((s): s is string => !!s && !!s.trim()))];
    const answer = await ask(keep ? ['idea'] : ['title', 'idea'], { shuffle: shuffles, ...(keep ? { keepTitle: keep } : {}), avoidIdeas });
    if (!answer) return;
    const cur = valueRef.current;
    setEditingIdea(false);
    setFocusIdea(false);
    commit({
      ...cur,
      title: keep ? cur.title : answer.title,
      idea: answer.idea,
      // Shuffling leaves any ready-made story.
      templateId: undefined,
      source: { ...cur.source, title: keep ? cur.source.title : 'suggestion', idea: 'suggestion' },
      suggested: {
        style: cur.suggested?.style ?? cur.style,
        title: keep ? cur.suggested?.title ?? cur.title : answer.title,
        idea: answer.idea,
      },
      suggestionSource: answer.source,
      shuffles,
      // Surprise me: an AI idea, not a card
      ideaCard: undefined,
      confirmed: keep ? { title: cur.confirmed?.title } : {},
    });
  };

  const handlePick = (t: UnifiedStoryTemplate) => {
    const v = valueRef.current;
    trackCreate('picture-book', 'template_picked', { template: t.id });
    commit({
      ...v,
      title: templateTitleFor(t, castFor(people)),
      idea: templateIdeaFor(t),
      templateId: t.id,
      source: { ...v.source, title: 'template', idea: 'template' },
      confirmed: {},
      ideaCard: undefined,
    });
    setPickerOpen(false);
  };

  const pickCard = (card: FilledIdea) => {
    commit(cardDetails(valueRef.current, card));
    setEditingIdea(false);
    setFocusIdea(false);
  };

  // Typing over a ready-made story's text makes it the parent's; a suggestion keeps its source so Next can tell
  // kept from edited.
  const editField = (field: 'title' | 'idea', text: string) =>
    commit({
      ...value,
      [field]: text,
      source: { ...value.source, [field]: value.source[field] === 'template' ? 'parent' : value.source[field] },
    });

  const toCheck = languageCheckFields({
    suggestionSource: value.suggestionSource,
    suggested: value.suggested,
    value,
    confirmed: value.confirmed ?? {},
    confirmedLanguage: value.confirmedLanguage,
  });
  // A confirmation is for the current language only; ones made in another language are dropped here.
  const confirm = (field: 'title' | 'idea') =>
    commit({
      ...value,
      confirmed: { ...(value.confirmedLanguage === value.language ? value.confirmed : {}), [field]: true },
      confirmedLanguage: value.language,
    });

  // Family from the photo (Task B3): a ready-made story about a person (grandparents-day: a grandparent) needs them
  // in the photo; the create route refuses it otherwise (B2). Shown at once, before the typed-relative notice; Next
  // stays disabled until the parent picks another story or changes the photo.
  const centralMissing = unfilledCentralRoles(
    value.templateId,
    people.map((p) => ({ name: p.name.trim(), age: p.age, isMainCharacter: p.isMainCharacter, relationship: p.relationship })),
  );
  // A live catalogue master: the book gets the catalogue title whatever is typed, so the field is read-only
  const titleFixed = templateTitleIsFixed(value.templateId);
  const centralTpl = centralMissing.length && value.templateId ? pictureBookTemplate(value.templateId) : undefined;
  const centralNotice = centralMissing.length
    ? centralRoleRefusal(
        storyNames?.[value.templateId!] ?? (centralTpl ? templateTitleFor(centralTpl, castFor(people)) : value.title.trim()),
        centralMissing,
      )
    : null;

  // Task C3: relatives the parent's idea names who are not in the photo (our template/suggestion text never counts)
  const relativeWords = thinking || centralNotice ? [] : pictureBookTypedRelatives(people, value);
  const gate = useTypedRelativeGate({
    flow: 'picture-book',
    words: relativeWords,
    choice: value.typedRelatives,
    onChoiceChange: (typedRelatives) => commit({ ...valueRef.current, typedRelatives }),
    onChangePhoto,
  });

  /** `chosen`: the parent just answered the typed-relative notice for these words. */
  const handleNext = (chosen = false) => {
    if (thinking || centralNotice) return;
    if (!value.title.trim()) {
      toast({ title: 'Book title needed', description: 'Give your book a title before we make the cover.', variant: 'destructive' });
      return;
    }
    if (toCheck.length > 0) {
      toast({
        title: 'Please check the wording',
        description: 'Edit the title and the idea, or confirm they are right in your language.',
        variant: 'destructive',
      });
      return;
    }
    if (!chosen && !gate.allowsNext()) return;
    // Once per suggestion/card and final text: Back then Next with nothing changed does not count twice
    const eventsKey = suggestionEventsKey(value);
    if (eventsKey && eventsKey !== value.eventsSent) {
      const source = ideaSourceOf(value);
      for (const e of suggestionEvents(value.suggested ?? null, value, value.source)) {
        trackCreate('picture-book', e.name, source ? { field: e.field, source } : { field: e.field });
      }
      // On the latest value: the typed-relative choice may have been committed in this same click
      commit({ ...valueRef.current, eventsSent: eventsKey });
    }
    onNext();
  };

  // An unedited suggestion (a card or the AI's) shows as text with "Edit the idea"; a ready-made story's idea and
  // the parent's own text keep the textarea
  const ideaCollapsed =
    !ideaPending &&
    !editingIdea &&
    value.source.idea === 'suggestion' &&
    !!value.suggested &&
    value.idea.trim() !== '' &&
    value.idea === value.suggested.idea;

  const offeredCounts = ALL_PAGE_COUNTS.filter((n) => allowedCounts.includes(n));
  const toggleClass = (active: boolean) =>
    `flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors border ${
      active ? 'bg-purple-600 text-white border-purple-600 shadow-sm' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
    }`;

  const languageNote = (field: 'title' | 'idea') =>
    toCheck.includes(field) && (
      <div className="flex flex-wrap items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900" role="note">
        <span>{CHECK_LANGUAGE}</span>
        <button type="button" className="font-semibold underline" onClick={() => confirm(field)}>
          It&apos;s right
        </button>
      </div>
    );

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">Your book</h2>
        <p className="text-gray-600 max-w-xl mx-auto">
          We&apos;ve suggested a style, a title and an idea for your family. Change anything you like.
        </p>
      </div>

      {centralNotice && (
        <div
          role="alert"
          data-central-role-notice=""
          className="space-y-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          <p>{centralNotice}</p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
              Pick another story
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={onChangePhoto}>
              Use a different photo
            </Button>
          </div>
        </div>
      )}

      {/* Style */}
      <fieldset>
        <legend className="flex items-center mb-2 text-sm font-medium text-gray-700">
          <Palette className="w-4 h-4 mr-2" aria-hidden />
          Art style
        </legend>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" role="radiogroup" aria-label="Art style">
          {PICTURE_BOOK_FLOW_STYLES.map((id) => {
            const selected = value.style === id;
            const preview = STYLE_PREVIEWS[id];
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => commit(withParentStyle(value, id))}
                className={`relative overflow-hidden rounded-lg border-2 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 ${
                  selected ? 'border-purple-500 shadow-md' : 'border-gray-200 hover:border-purple-200'
                }`}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={preview} alt="" className="aspect-[2/3] w-full object-cover" loading="lazy" />
                <span className="flex items-center justify-between px-2 py-1.5 text-sm font-semibold text-gray-800">
                  {pictureBookStyleName(id)}
                  {selected && (
                    <span className="bg-purple-600 rounded-full p-0.5">
                      <Check className="w-3 h-3 text-white" aria-hidden />
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </fieldset>

      {/* Title + idea */}
      <div className="space-y-4">
        {thinking && (
          <p className="flex items-center text-sm text-purple-700" role="status">
            <Sparkles className="w-4 h-4 mr-2" aria-hidden />
            {THINKING}
          </p>
        )}
        <div className="space-y-1">
          <Label htmlFor="pb-title" className="flex items-center text-gray-700">
            <BookOpen className="w-4 h-4 mr-2" aria-hidden />
            Book title <span className="text-red-500 ml-1">*</span>
          </Label>
          <Input
            id="pb-title"
            value={titlePending ? '' : value.title}
            placeholder={titlePending ? THINKING : 'e.g. Mia and the Moon Garden'}
            disabled={titlePending}
            aria-busy={titlePending}
            maxLength={80}
            readOnly={titleFixed}
            aria-describedby={titleFixed ? 'pb-title-fixed' : undefined}
            onChange={(e) => { if (!titleFixed) editField('title', e.target.value); }}
          />
          {titleFixed && (
            <p id="pb-title-fixed" className="text-sm text-gray-500">
              {FIXED_TITLE_NOTE}
            </p>
          )}
          {languageNote('title')}
        </div>

        <div className="space-y-1">
          {ideaCollapsed ? (
            // Collapsed: no textarea yet, so the label is plain text that names the summary group
            <p id="pb-idea-label" className="text-sm font-medium leading-none text-gray-700">
              Story idea
            </p>
          ) : (
            <Label htmlFor="pb-idea" className="text-gray-700">
              Story idea
            </Label>
          )}
          {ideaCollapsed ? (
            <div role="group" aria-labelledby="pb-idea-label" className="space-y-2">
              <p data-idea-summary="" className="text-sm text-gray-700">{value.idea}</p>
              <button
                type="button"
                onClick={() => {
                  setEditingIdea(true);
                  setFocusIdea(true);
                }}
                className="inline-flex min-h-11 items-center gap-1.5 rounded-sm text-sm font-medium text-purple-700 underline underline-offset-4 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-marigold-deep"
              >
                <Pencil className="w-4 h-4" aria-hidden="true" />
                Edit the idea
              </button>
            </div>
          ) : (
            <Textarea
              id="pb-idea"
              value={ideaPending ? '' : value.idea}
              placeholder={ideaPending ? THINKING : 'What should happen in the story?'}
              disabled={ideaPending}
              aria-busy={ideaPending}
              rows={4}
              maxLength={500}
              // Only after "Edit the idea": the button that had the focus is gone, so the textarea takes it
              autoFocus={focusIdea}
              onChange={(e) => editField('idea', e.target.value)}
            />
          )}
          {languageNote('idea')}
        </div>

        {!value.templateId && (
          <IdeaCardPicker
            cards={cards}
            // Only a listed card: one that dropped out stays unchecked while the AI re-asks
            selectedId={value.ideaCard && cards.some((c) => c.id === value.ideaCard) ? value.ideaCard : null}
            onPick={pickCard}
            onSurprise={() => void handleShuffle()}
            disabled={thinking}
          />
        )}

        <Button type="button" variant="outline" onClick={() => setPickerOpen(true)} disabled={thinking}>
          Pick a ready-made story
        </Button>
        <PictureBookTemplatePicker open={pickerOpen} onOpenChange={setPickerOpen} people={people} onPick={handlePick} />
      </div>

      {/* Page count */}
      <div>
        <Label className="mb-2 block text-gray-700" id="pb-pages-label">
          Pages
        </Label>
        {offeredCounts.length === 1 ? (
          <p className="text-sm font-medium text-gray-700">{offeredCounts[0]} pages</p>
        ) : (
          <div className="flex gap-2 max-w-md" role="radiogroup" aria-labelledby="pb-pages-label">
            {offeredCounts.map((n) => (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={value.pageCount === n}
                className={toggleClass(value.pageCount === n)}
                onClick={() => commit({ ...value, pageCount: n, pageCountChosen: true })}
              >
                {n} pages
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Language */}
      <div className="space-y-1">
        <Label htmlFor="pb-language" className="flex items-center text-gray-700">
          <Globe className="w-4 h-4 mr-2" aria-hidden />
          Story language
        </Label>
        <Select value={value.language} onValueChange={(language) => commit({ ...value, language })}>
          <SelectTrigger id="pb-language" className="w-full">
            <SelectValue placeholder="Select a language" />
          </SelectTrigger>
          <SelectContent>
            {getOfferedLanguageOptions(value.language).map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {gate.showing && (
        <TypedRelativeNotice
          words={relativeWords}
          onChangePhoto={gate.changePhoto}
          onContinue={() => {
            gate.continueWithout();
            handleNext(true);
          }}
        />
      )}

      <div className="flex flex-col-reverse sm:flex-row justify-between gap-3">
        <Button variant="outline" size="lg" onClick={onBack} className="w-full sm:w-auto">
          Back
        </Button>
        <Button
          size="lg"
          className="bg-purple-600 hover:bg-purple-700 text-white w-full sm:w-auto"
          onClick={() => handleNext()}
          disabled={thinking || busy || gate.showing || !!centralNotice}
          aria-busy={busy || undefined}
        >
          {busy ? 'Opening your book…' : 'Make my cover'}
        </Button>
      </div>
    </div>
  );
}
